// Scoped override wins; otherwise this node's placements, then plan-wide leftovers. Callers must share this so tree/layout/detector stay aligned.
import type { StudioDraft, StudioItem } from "./studioTypes";

export interface OrderCarrier {
  value: string | null | undefined;
  placements: StudioItem["placements"];
}

export const readingOrderOfCarriers = (carriers: readonly OrderCarrier[]): string[] => {
  const entries: { run: number; shelf: number; xCm: number; value: string }[] = [];
  for (const carrier of carriers) {
    const value = carrier.value;
    if (value === null || value === undefined || value.trim() === "") continue;
    for (const placement of carrier.placements) {
      entries.push({ run: placement.run, shelf: placement.shelf, xCm: placement.xCm, value });
    }
  }
  entries.sort((a, b) => a.run - b.run || a.shelf - b.shelf || a.xCm - b.xCm);
  const seen = new Set<string>();
  const order: string[] = [];
  for (const entry of entries) {
    if (!seen.has(entry.value)) {
      seen.add(entry.value);
      order.push(entry.value);
    }
  }
  return order;
};

export const nodeValueOrder = (
  draft: Pick<StudioDraft, "scopedValueOrder" | "valueOrder">,
  /** Lookup keys in priority; drafts may store an override under a treeProjection alias. */
  pathKeys: readonly string[],
  attr: string,
  carriers: readonly OrderCarrier[],
): readonly string[] => {
  for (const key of pathKeys) {
    const scoped = draft.scopedValueOrder?.[key];
    if (scoped && scoped.length > 0) return scoped;
  }
  const measured = readingOrderOfCarriers(carriers);
  const plan = draft.valueOrder[attr] ?? [];
  if (measured.length === 0) return plan;
  const seen = new Set(measured);
  return [...measured, ...plan.filter((value) => !seen.has(value))];
};
